import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { FilteredState, PaginationParams, SortParams, FilterParams } from '../types';
import { appConfig } from '@/config/app-config';
export interface Carrier {
  id: number;
  short_name: string;
  official_name: string | null;
  scac: string | null;
  avatar_url: string | null;
  is_active: boolean;
  created_at: string;
  created_by: string | null;
  updated_at: string | null;
  updated_by: string | null;
}
// Interfaz para opciones de filtro (usado en dropdowns)
export interface CarrierFilterOption {
  value: string;
  label: string;
  avatar_url?: string;
}
interface CarriersState extends FilteredState<Carrier> {
  selectedCarrier: Carrier | null;
  creating: boolean;
  updating: boolean;
  deleting: boolean;
  // Estado para opciones de filtro (cacheadas para evitar recargas)
  filterOptions: CarrierFilterOption[];
  filterOptionsLoading: boolean;
  filterOptionsError: string | null;
  filterOptionsLastFetch: number | null;
}
const initialState: CarriersState = {
  items: [],
  totalCount: 0,
  currentPage: 1,
  pageSize: 50,
  loading: false,
  error: null,
  lastFetch: null,
  filters: {},
  sortField: 'short_name',
  sortDirection: 'asc',
  searchTerm: null,
  selectedCarrier: null,
  creating: false,
  updating: false,
  deleting: false,
  // Estado para opciones de filtro
  filterOptions: [],
  filterOptionsLoading: false,
  filterOptionsError: null,
  filterOptionsLastFetch: null,
};
// Tipos de respuesta
interface CarriersResponse {
  data: Carrier[];
  totalCount: number;
}
// Thunk para cargar carriers con paginación y filtros (para vistas de lista)
export const fetchCarriers = createAsyncThunk<CarriersResponse, any>('carriers/fetchCarriers', async (params) => {
  throw new Error('Not implemented yet');
});
// Thunk para cargar opciones de filtro (solo id, nombre, avatar) - CACHEADO
export const fetchCarrierFilterOptions = createAsyncThunk<CarrierFilterOption[], void>(
  'carriers/fetchFilterOptions',
  async (_, { getState, rejectWithValue }) => {
    const state = getState() as any;
    const carriersState = state.carriers as CarriersState;
    // ✅ CACHE: Si ya tenemos datos frescos (menos de 5 minutos), usar cache
    const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos
    if (
      carriersState.filterOptionsLastFetch &&
      Date.now() - carriersState.filterOptionsLastFetch < CACHE_DURATION &&
      carriersState.filterOptions.length > 0
    ) {
      return carriersState.filterOptions;
    }
    try {
      // Obtener token de autenticación (de donde esté disponible)
      const token = localStorage.getItem('authToken') || '';
      const url = appConfig.getApiUrl('masters/carriers?startRow=0&endRow=500&sortProperty=shortName&sortDirection=asc');
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(`Error ${response.status}: ${errorText}`);
      }
      const result = await response.json();
      const carriers = result?.data?.rows || [];
      // Transformar a formato de opciones de filtro
      const filterOptions: CarrierFilterOption[] = carriers.map((carrier: any) => ({
        value: carrier.id.toString(),
        label: carrier.short_name,
        avatar_url: carrier.avatar_url
      }));
      return filterOptions;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Error al cargar opciones de filtro');
    }
  }
);
export const fetchCarrierById = createAsyncThunk<Carrier, number>('carriers/fetchCarrierById', async (id) => {
  throw new Error('Not implemented yet');
});
export const createCarrier = createAsyncThunk<Carrier, Partial<Carrier>>('carriers/createCarrier', async (carrier) => {
  throw new Error('Not implemented yet');
});
export const updateCarrier = createAsyncThunk<{ id: number; data: Carrier }, { id: number; data: Partial<Carrier> }>('carriers/updateCarrier', async ({ id, data }) => {
  throw new Error('Not implemented yet');
});
export const deleteCarrier = createAsyncThunk<number, number>('carriers/deleteCarrier', async (id) => {
  throw new Error('Not implemented yet');
});
const carriersSlice = createSlice({
  name: 'carriers',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<FilterParams>) => {
      state.filters = { ...state.filters, ...action.payload };
      state.currentPage = 1;
    },
    clearFilters: (state) => {
      state.filters = {};
      state.searchTerm = null;
      state.currentPage = 1;
    },
    setSearchTerm: (state, action: PayloadAction<string | null>) => {
      state.searchTerm = action.payload;
      state.currentPage = 1;
    },
    setSort: (state, action: PayloadAction<SortParams>) => {
      state.sortField = action.payload.field;
      state.sortDirection = action.payload.direction;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
      state.currentPage = 1;
    },
    selectCarrier: (state, action: PayloadAction<Carrier | null>) => {
      state.selectedCarrier = action.payload;
    },
    clearSelection: (state) => {
      state.selectedCarrier = null;
    },
  },
  extraReducers: (builder) => {
    // Casos para fetchCarriers (lista completa)
    builder
      .addCase(fetchCarriers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCarriers.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.data;
        state.totalCount = action.payload.totalCount;
        state.lastFetch = Date.now();
      })
      .addCase(fetchCarriers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Error al cargar navieras';
      })
      // Casos para fetchCarrierFilterOptions (opciones cacheadas)
      .addCase(fetchCarrierFilterOptions.pending, (state) => {
        state.filterOptionsLoading = true;
        state.filterOptionsError = null;
      })
      .addCase(fetchCarrierFilterOptions.fulfilled, (state, action) => {
        state.filterOptionsLoading = false;
        state.filterOptions = action.payload;
        state.filterOptionsLastFetch = Date.now();
      })
      .addCase(fetchCarrierFilterOptions.rejected, (state, action) => {
        state.filterOptionsLoading = false;
        state.filterOptionsError = action.payload as string || 'Error al cargar opciones de filtro';
      });
  },
});
// Selectores para acceder al estado
export const selectCarrierFilterOptions = (state: any) => state.carriers.filterOptions;
export const selectCarrierFilterOptionsLoading = (state: any) => state.carriers.filterOptionsLoading;
export const selectCarrierFilterOptionsError = (state: any) => state.carriers.filterOptionsError;
// Helper para verificar si las opciones de filtro están frescas
export const areCarrierFilterOptionsFresh = (state: any): boolean => {
  const lastFetch = state.carriers.filterOptionsLastFetch;
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos
  return lastFetch && Date.now() - lastFetch < CACHE_DURATION;
};
export const { setFilters, clearFilters, setSearchTerm, setSort, setPage, setPageSize, selectCarrier, clearSelection } = carriersSlice.actions;
export default carriersSlice.reducer;
